import { useDataTableFilters } from '@components/data-table-filter';
import { ActiveFilters } from '@components/data-table-filter/components/active-filters';
import { FilterActions } from '@components/data-table-filter/components/filter-actions';
import { FilterSelector } from '@components/data-table-filter/components/filter-selector';
import type { ColumnOption, FiltersState } from '@components/data-table-filter/core/types';
import { Button } from '@components/ui/button';
import { ClearableSearchInput } from '@components/ui/clearable-search-input';
import { Label } from '@components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select';
import { Separator } from '@components/ui/separator';
import { useGetCategories } from '@queries/category.query';
import { useGetDomains } from '@queries/domain.query';
import { useIntelTechnologyCompaniesQuery } from '@queries/intel-technologies.query';
import { useGetUses } from '@queries/use.query';
import { X } from 'lucide-react';
import type { FC, SetStateAction } from 'react';
import { useMemo } from 'react';
import {
  type IntelTechnologyFilterRow,
  intelTechnologiesFilterColumnsConfig,
} from '../intel-technologies-filter-columns.config';

const SCORE_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9];
const emptyFilterRows: IntelTechnologyFilterRow[] = [];

type IntelTechnologiesFilterBarProps = {
  filtersState: FiltersState;
  mrlMax: number | null;
  mrlMin: number | null;
  onFiltersChange: (filters: SetStateAction<FiltersState>) => void;
  search: string | null;
  setMrlMax: (value: number | null) => Promise<URLSearchParams>;
  setMrlMin: (value: number | null) => Promise<URLSearchParams>;
  setPage: (page: number) => Promise<URLSearchParams>;
  setSearch: (search: string | null) => Promise<URLSearchParams>;
  setTrlMax: (value: number | null) => Promise<URLSearchParams>;
  setTrlMin: (value: number | null) => Promise<URLSearchParams>;
  trlMax: number | null;
  trlMin: number | null;
};

type ScoreRangeFilterProps = {
  label: string;
  max: number | null;
  min: number | null;
  onMaxChange: (value: number | null) => void;
  onMinChange: (value: number | null) => void;
};

type FilterPillProps = {
  label: string;
  onClear: () => void;
  value: string;
};

const toSelectValue = (value: number | null) => (value ? String(value) : 'any');

const fromSelectValue = (value: string) => (value === 'any' ? null : Number(value));

const formatScoreRange = (params: { max: number | null; min: number | null }) => {
  const { max, min } = params;

  if (min !== null && max !== null) return `${min}-${max}`;
  if (min !== null) return `>= ${min}`;
  if (max !== null) return `<= ${max}`;
  return '';
};

const FilterPill: FC<FilterPillProps> = (props) => {
  const { label, onClear, value } = props;

  return (
    <div className="flex h-7 items-center rounded-2xl border border-border bg-background shadow-xs text-xs">
      <span className="px-2 text-muted-foreground">{label}</span>
      <Separator orientation="vertical" />
      <span className="px-2">{value}</span>
      <Separator orientation="vertical" />
      <Button
        className="rounded-none rounded-r-2xl text-xs w-7 h-full"
        onClick={onClear}
        type="button"
        variant="ghost"
      >
        <X className="size-4 -translate-x-0.5" />
      </Button>
    </div>
  );
};

const ScoreRangeFilter: FC<ScoreRangeFilterProps> = (props) => {
  const { label, max, min, onMaxChange, onMinChange } = props;

  return (
    <div className="grid gap-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="grid grid-cols-2 gap-2">
        <Select
          value={toSelectValue(min)}
          onValueChange={(value) => onMinChange(fromSelectValue(value))}
        >
          <SelectTrigger className="h-8 w-full">
            <SelectValue placeholder="Min" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Min</SelectItem>
            {SCORE_OPTIONS.map((option) => (
              <SelectItem
                key={option}
                value={String(option)}
              >
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={toSelectValue(max)}
          onValueChange={(value) => onMaxChange(fromSelectValue(value))}
        >
          <SelectTrigger className="h-8 w-full">
            <SelectValue placeholder="Max" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Max</SelectItem>
            {SCORE_OPTIONS.map((option) => (
              <SelectItem
                key={option}
                value={String(option)}
              >
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export const IntelTechnologiesFilterBar: FC<IntelTechnologiesFilterBarProps> = (props) => {
  const {
    filtersState,
    mrlMax,
    mrlMin,
    onFiltersChange,
    search,
    setMrlMax,
    setMrlMin,
    setPage,
    setSearch,
    setTrlMax,
    setTrlMin,
    trlMax,
    trlMin,
  } = props;

  const { data: categories } = useGetCategories();
  const { data: companiesData } = useIntelTechnologyCompaniesQuery({ enabled: true });
  const { data: domains } = useGetDomains();
  const { data: uses } = useGetUses();

  const hasSearchFilter = Boolean(search?.trim());
  const hasScoreFilters = trlMin !== null || trlMax !== null || mrlMin !== null || mrlMax !== null;
  const hasActivePills = filtersState.length > 0 || hasSearchFilter || hasScoreFilters;

  const companyOptions = useMemo<ColumnOption[]>(
    () => (companiesData?.companies ?? []).map((company) => ({ label: company.name, value: company.id })),
    [companiesData],
  );
  const categoryOptions = useMemo<ColumnOption[]>(
    () => (categories ?? []).map((category) => ({ label: category.name, value: category.id })),
    [categories],
  );
  const domainOptions = useMemo<ColumnOption[]>(
    () => (domains ?? []).map((domain) => ({ label: domain.name, value: domain.id })),
    [domains],
  );
  const subcategoryOptions = useMemo<ColumnOption[]>(
    () =>
      (categories ?? []).flatMap((category) =>
        category.subcategories.map((subcategory) => ({
          label: `${category.name} / ${subcategory.name}`,
          value: subcategory.id,
        })),
      ),
    [categories],
  );
  const useOptions = useMemo<ColumnOption[]>(
    () => (uses ?? []).map((use) => ({ label: use.name, value: use.id })),
    [uses],
  );

  const { actions, columns, filters, strategy } = useDataTableFilters({
    columnsConfig: intelTechnologiesFilterColumnsConfig,
    data: emptyFilterRows,
    filters: filtersState,
    onFiltersChange,
    options: {
      categoryIds: categoryOptions,
      companyIds: companyOptions,
      domainIds: domainOptions,
      subcategoryIds: subcategoryOptions,
      useIds: useOptions,
    },
    strategy: 'server',
  });

  const readinessFilterContent = (
    <div className="grid w-[240px] gap-3 border-t p-3">
      <ScoreRangeFilter
        label="TRL"
        max={trlMax}
        min={trlMin}
        onMaxChange={(value) => {
          void setTrlMax(value);
          if (value !== null && trlMin !== null && trlMin > value) void setTrlMin(null);
          void setPage(1);
        }}
        onMinChange={(value) => {
          void setTrlMin(value);
          if (value !== null && trlMax !== null && trlMax < value) void setTrlMax(null);
          void setPage(1);
        }}
      />
      <ScoreRangeFilter
        label="MRL"
        max={mrlMax}
        min={mrlMin}
        onMaxChange={(value) => {
          void setMrlMax(value);
          if (value !== null && mrlMin !== null && mrlMin > value) void setMrlMin(null);
          void setPage(1);
        }}
        onMinChange={(value) => {
          void setMrlMin(value);
          if (value !== null && mrlMax !== null && mrlMax < value) void setMrlMax(null);
          void setPage(1);
        }}
      />
    </div>
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <FilterSelector
          actions={actions}
          active={hasScoreFilters}
          additionalContent={readinessFilterContent}
          columns={columns}
          filters={filters}
          strategy={strategy}
        />
        <ClearableSearchInput
          clearValue={null}
          placeholder="Search by technology name"
          value={search}
          onChange={(value) => {
            void setSearch(value);
            void setPage(1);
          }}
        />
      </div>
      {hasActivePills && (
        <div className="flex w-full items-start justify-between gap-2">
          <div className="flex md:flex-wrap gap-2 w-full flex-1">
            {hasSearchFilter && (
              <FilterPill
                label="Search"
                onClear={() => {
                  void setSearch(null);
                  void setPage(1);
                }}
                value={search ?? ''}
              />
            )}
            <ActiveFilters
              actions={actions}
              columns={columns}
              filters={filters}
              strategy={strategy}
            />
            {(trlMin !== null || trlMax !== null) && (
              <FilterPill
                label="TRL"
                onClear={() => {
                  void setTrlMin(null);
                  void setTrlMax(null);
                  void setPage(1);
                }}
                value={formatScoreRange({ max: trlMax, min: trlMin })}
              />
            )}
            {(mrlMin !== null || mrlMax !== null) && (
              <FilterPill
                label="MRL"
                onClear={() => {
                  void setMrlMin(null);
                  void setMrlMax(null);
                  void setPage(1);
                }}
                value={formatScoreRange({ max: mrlMax, min: mrlMin })}
              />
            )}
          </div>
          <FilterActions
            actions={actions}
            hasFilters={filters.length > 0}
          />
        </div>
      )}
    </div>
  );
};
